// Documents → Faxes: everything faxed about this client, both directions,
// newest first. An outbound request shows its checklist; a reply that
// answered one says so. "Request documents" is the PPOT / LMN /
// records letter with reply matching. Unsent drafts sit at the top.
import { useCallback, useEffect, useState } from 'react';
import { getClientFaxes, outstandingItems } from '../../lib/faxes';
import { listClientFaxDrafts } from '../../lib/faxDrafts';
import FaxDraftRow from '../faxes/FaxDraftRow';
import FaxRow from '../faxes/FaxRow';
import AssignFaxModal from '../faxes/AssignFaxModal';
import SendFaxModal from '../faxes/SendFaxModal';

export default function ClientFaxesTab({ client, onRequest, refreshKey }) {
  const [faxes, setFaxes] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [sending, setSending] = useState(false);      // false | true (new) | a draft row
  const [drafts, setDrafts] = useState([]);

  const load = useCallback(async () => {
    const [{ data }, { data: d }] = await Promise.all([getClientFaxes(client.id), listClientFaxDrafts(client.id)]);
    setFaxes(data || []);
    setDrafts(d || []);
  }, [client.id]);
  // A request draft opens in the page's Request documents window, which has
  // the child's Drive folder; a plain fax draft opens here.
  const resume = (draft) => (draft.kind === 'request' ? onRequest(draft) : setSending(draft));
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const tick = () => { if (document.visibilityState === 'visible') load(); };
    const id = setInterval(tick, 30_000);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', tick); };
  }, [load]);

  const open = (faxes || []).filter((f) => f.direction === 'outbound' && (['failure', 'error'].includes(f.status) || (['pending', 'success'].includes(f.status) && outstandingItems(f).length > 0)));

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{client.first_name}'s faxes</div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            <b>Send a fax</b> to fax files out. <b>Request documents</b> to ask a provider for a PPOT, letter of medical necessity or records — replies tick the request they answer.
            {open.length > 0 && <> · <b style={{ color: 'var(--warn)' }}>{open.length} request{open.length === 1 ? '' : 's'} waiting on a reply</b></>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={() => setSending(true)} title="Any files to any number, with a cover page">Send a fax</button>
          <button className="btn btn-primary btn-sm" onClick={() => onRequest()} title="Ask a provider for the PPOT (Appendix T), a letter of medical necessity or medical records — CAERA writes the letter and ticks each item off as it comes back">Request documents</button>
        </div>
      </div>
      {drafts.length > 0 && (
        <div style={{ border: '1px solid var(--warn)', borderRadius: 12, background: 'var(--card-bg)', padding: '4px 16px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--warn)', padding: '10px 0 2px' }}>
            {drafts.length} unsent draft{drafts.length === 1 ? '' : 's'} — saved automatically, pick up where you left off
          </div>
          {drafts.map((d, i) => <FaxDraftRow key={d.id} draft={d} last={i === drafts.length - 1} onResume={resume} onChanged={load} />)}
        </div>
      )}
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, background: 'var(--card-bg)', padding: '4px 16px' }}>
        {faxes === null && <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 0' }}>Loading…</div>}
        {faxes && faxes.length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)', padding: '14px 0' }}>Nothing yet. Faxes about {client.first_name} appear here as they arrive, and anything sent from here is listed too.</div>}
        {(faxes || []).map((f, i) => <FaxRow key={f.id} fax={f} last={i === faxes.length - 1} onAssign={setAssigning} onChanged={load} />)}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text3)' }}>Received faxes are filed in the <b>Faxes</b> folder automatically; sent letters too. The agency-wide inbox is under General → Fax Manager.</div>
      {assigning && <AssignFaxModal fax={assigning} onClose={() => setAssigning(null)} onDone={load} />}
      {sending && <SendFaxModal client={client} draft={sending === true ? null : sending} onClose={() => { setSending(false); load(); }} onSent={() => { setSending(false); load(); }} />}
    </div>
  );
}
